import type { RefundRequest } from '../types'
import { formatNaira } from './formatCurrency'
import { formatDate } from './dateUtils'

export function generateReceiptHTML(refundRequest: RefundRequest): string {
  const {
    ticket,
    refundOption,
    originalAmount,
    processingFee,
    netRefund,
    bonusAmount,
    createdAt,
    completedAt,
    id,
  } = refundRequest

  return `
<!DOCTYPE html>
<html>
<head>
  <meta charset="UTF-8">
  <title>AeroZeep Refund Receipt - ${id}</title>
  <style>
    * { margin: 0; padding: 0; box-sizing: border-box; }
    body { 
      font-family: 'Montserrat', system-ui, -apple-system, sans-serif; 
      line-height: 1.6; 
      color: #1A1A1A;
      padding: 40px 20px;
      background: #F5F7FA;
    }
    .container { 
      max-width: 600px; 
      margin: 0 auto; 
      background: white; 
      border-radius: 16px; 
      box-shadow: 0 4px 20px rgba(0,0,0,0.08);
      overflow: hidden;
    }
    .header { 
      background: linear-gradient(135deg, #0066FF 0%, #0052CC 100%); 
      color: white; 
      padding: 32px; 
      text-align: center;
    }
    .logo { 
      font-size: 28px; 
      font-weight: 800; 
      margin-bottom: 8px;
      display: flex;
      align-items: center;
      justify-content: center;
      gap: 12px;
    }
    .logo-icon {
      width: 40px;
      height: 40px;
      background: white;
      border-radius: 12px;
      display: inline-flex;
      align-items: center;
      justify-content: center;
    }
    .subtitle { 
      font-size: 14px; 
      opacity: 0.9;
    }
    .content { 
      padding: 32px;
    }
    .success-badge {
      display: inline-flex;
      align-items: center;
      gap: 8px;
      background: #E6FFF0;
      color: #00C853;
      padding: 8px 16px;
      border-radius: 8px;
      font-size: 14px;
      font-weight: 600;
      margin-bottom: 24px;
    }
    .section { 
      margin-bottom: 24px;
      padding-bottom: 24px;
      border-bottom: 1px solid #E0E0E0;
    }
    .section:last-child {
      border-bottom: none;
      margin-bottom: 0;
      padding-bottom: 0;
    }
    .section-title { 
      font-size: 14px; 
      font-weight: 600; 
      color: #5C6B7A; 
      margin-bottom: 12px;
      text-transform: uppercase;
      letter-spacing: 0.5px;
    }
    .info-row { 
      display: flex; 
      justify-content: space-between; 
      padding: 8px 0;
      font-size: 15px;
    }
    .info-label { 
      color: #5C6B7A;
    }
    .info-value { 
      font-weight: 600; 
      color: #1A1A1A;
      text-align: right;
    }
    .total-row {
      background: #F5F7FA;
      padding: 16px;
      border-radius: 12px;
      margin-top: 16px;
    }
    .total-row .info-label {
      font-size: 16px;
      font-weight: 600;
      color: #1A1A1A;
    }
    .total-row .info-value {
      font-size: 20px;
      font-weight: 800;
      color: #0066FF;
    }
    .bonus-highlight {
      color: #FFB300;
      font-weight: 700;
    }
    .footer { 
      background: #F5F7FA; 
      padding: 24px 32px; 
      text-align: center; 
      font-size: 13px; 
      color: #5C6B7A;
      line-height: 1.8;
    }
    .footer-badges {
      display: flex;
      justify-content: center;
      gap: 16px;
      margin-top: 16px;
      flex-wrap: wrap;
    }
    .badge {
      display: inline-flex;
      align-items: center;
      gap: 4px;
      font-size: 12px;
      color: #5C6B7A;
    }
    @media print {
      body { padding: 0; background: white; }
      .container { box-shadow: none; }
    }
  </style>
</head>
<body>
  <div class="container">
    <div class="header">
      <div class="logo">
        <div class="logo-icon">✈️</div>
        AeroZeep
      </div>
      <div class="subtitle">Refund Receipt</div>
    </div>
    
    <div class="content">
      <div style="text-align: center;">
        <div class="success-badge">
          ✓ Refund Completed
        </div>
      </div>

      <div class="section">
        <div class="section-title">Refund Details</div>
        <div class="info-row">
          <span class="info-label">Reference Number</span>
          <span class="info-value">${id}</span>
        </div>
        <div class="info-row">
          <span class="info-label">Date Processed</span>
          <span class="info-value">${formatDate(completedAt || createdAt, { includeTime: true })}</span>
        </div>
        <div class="info-row">
          <span class="info-label">Refund Method</span>
          <span class="info-value">${refundOption === 'store_credit' ? 'Store Credit' : 'Bank Payout'}</span>
        </div>
      </div>

      <div class="section">
        <div class="section-title">Flight Information</div>
        <div class="info-row">
          <span class="info-label">Airline</span>
          <span class="info-value">${ticket.flight.airline.name}</span>
        </div>
        <div class="info-row">
          <span class="info-label">Flight Number</span>
          <span class="info-value">${ticket.flight.flightNumber}</span>
        </div>
        <div class="info-row">
          <span class="info-label">Route</span>
          <span class="info-value">${ticket.flight.originCity} → ${ticket.flight.destinationCity}</span>
        </div>
        <div class="info-row">
          <span class="info-label">Ticket Number</span>
          <span class="info-value">${ticket.ticketNumber}</span>
        </div>
        <div class="info-row">
          <span class="info-label">Passenger</span>
          <span class="info-value">${ticket.passenger.firstName} ${ticket.passenger.lastName}</span>
        </div>
      </div>

      <div class="section">
        <div class="section-title">Payment Breakdown</div>
        <div class="info-row">
          <span class="info-label">Original Ticket Amount</span>
          <span class="info-value">${formatNaira(originalAmount)}</span>
        </div>
        <div class="info-row">
          <span class="info-label">Processing Fee</span>
          <span class="info-value">-${formatNaira(processingFee)}</span>
        </div>
        <div class="info-row">
          <span class="info-label">Net Refund</span>
          <span class="info-value">${formatNaira(netRefund)}</span>
        </div>
        ${
          bonusAmount
            ? `
        <div class="info-row">
          <span class="info-label">Store Credit Bonus (10%)</span>
          <span class="info-value bonus-highlight">+${formatNaira(bonusAmount)}</span>
        </div>
        `
            : ''
        }
        <div class="total-row">
          <div class="info-row" style="padding: 0;">
            <span class="info-label">Total Amount</span>
            <span class="info-value">${formatNaira(netRefund + (bonusAmount || 0))}</span>
          </div>
        </div>
      </div>

      ${
        refundOption === 'bank_payout' && refundRequest.bankDetails
          ? `
      <div class="section">
        <div class="section-title">Bank Details</div>
        <div class="info-row">
          <span class="info-label">Bank</span>
          <span class="info-value">${refundRequest.bankDetails.bankName}</span>
        </div>
        <div class="info-row">
          <span class="info-label">Account Number</span>
          <span class="info-value">${refundRequest.bankDetails.accountNumber}</span>
        </div>
        <div class="info-row">
          <span class="info-label">Account Name</span>
          <span class="info-value">${refundRequest.bankDetails.accountName}</span>
        </div>
      </div>
      `
          : ''
      }
    </div>

    <div class="footer">
      <p>This is an official receipt from AeroZeep</p>
      <p>For support, contact us at support@aerozeep.com or +234 800 000 0000</p>
      <div class="footer-badges">
        <span class="badge">🛡️ NCAA Compliant</span>
        <span class="badge">🔒 NDPR Secure</span>
        <span class="badge">✓ PCI-DSS Certified</span>
      </div>
    </div>
  </div>
</body>
</html>
  `
}

export function downloadReceipt(refundRequest: RefundRequest): void {
  const html = generateReceiptHTML(refundRequest)
  const blob = new Blob([html], { type: 'text/html' })
  const url = URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.href = url
  link.download = `AeroZeep-Receipt-${refundRequest.id}.html`
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)
  URL.revokeObjectURL(url)
}
